import { useEffect } from 'react';

const ERROR_ALERT_SELECTOR = '.ant-alert-error';
const STICKY_HEADER_SELECTOR = '.private-header';
const VIEWPORT_GAP = 12;

const collectErrorAlerts = (mutations) => {
  const alerts = new Set();

  mutations.forEach((mutation) => {
    const target = mutation.target.nodeType === Node.ELEMENT_NODE
      ? mutation.target
      : mutation.target.parentElement;
    const containingAlert = target?.closest?.(ERROR_ALERT_SELECTOR);
    if (containingAlert) {
      alerts.add(containingAlert);
    }

    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      if (node.matches(ERROR_ALERT_SELECTOR)) {
        alerts.add(node);
      }
      node.querySelectorAll(ERROR_ALERT_SELECTOR).forEach((alert) => alerts.add(alert));
    });
  });

  return [...alerts];
};

const getStickyHeaderBottom = () => {
  const header = document.querySelector(STICKY_HEADER_SELECTOR);
  return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
};

/**
 * Error alerts are rendered at the top of each page, so when an action fails while
 * the user is scrolled down the message appears off-screen. Watches the page content
 * and scrolls up to any error alert that appears (or changes text) above the viewport.
 */
const useScrollToErrorAlerts = (containerRef, enabled = true) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container || typeof MutationObserver === 'undefined') {
      return undefined;
    }

    let timer = null;
    let pending = [];

    const scrollToTopmostHiddenAlert = () => {
      timer = null;
      const alerts = pending.filter((alert) => alert.isConnected);
      pending = [];

      const headerBottom = getStickyHeaderBottom();
      const hidden = alerts
        .map((alert) => ({ alert, rect: alert.getBoundingClientRect() }))
        .filter(({ rect }) => rect.height > 0)
        .filter(({ rect }) => rect.top < headerBottom)
        .sort((a, b) => a.rect.top - b.rect.top);

      if (!hidden.length) {
        return;
      }

      const { rect } = hidden[0];
      window.scrollTo({
        top: Math.max(0, window.scrollY + rect.top - headerBottom - VIEWPORT_GAP),
        behavior: 'smooth',
      });
    };

    const observer = new MutationObserver((mutations) => {
      const alerts = collectErrorAlerts(mutations);
      if (!alerts.length) {
        return;
      }
      pending.push(...alerts);
      if (timer === null) {
        timer = window.setTimeout(scrollToTopmostHiddenAlert, 0);
      }
    });

    observer.observe(container, { childList: true, subtree: true, characterData: true });

    return () => {
      observer.disconnect();
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [containerRef, enabled]);
};

export default useScrollToErrorAlerts;
